import { useState, useCallback } from 'react';
import { apiFetch } from '@/lib/apiFetch';

export interface IntegrityCheckRun {
  id: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'done' | 'failed';
  result: {
    passed: number;
    failed: number;
    total: number;
    checks: Array<{ name: string; pass: boolean; detail: string }>;
  } | null;
  error: string | null;
}

export function useIntegrityChecks() {
  const [latest, setLatest] = useState<IntegrityCheckRun | null>(null);
  const [current, setCurrent] = useState<IntegrityCheckRun | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await apiFetch('/api/admin/integrity-check/latest');
      if (res.ok) {
        const json = await res.json();
        setLatest(json.data ?? null);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const trigger = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/api/admin/integrity-check', {
        method: 'POST',
      });
      if (!res.ok) throw new Error('Failed to trigger integrity check');
      const json = await res.json();
      const runId: string = json.data?.runId;
      if (!runId) throw new Error('No run ID returned');

      // Poll for completion
      const poll = async () => {
        const pollRes = await apiFetch(`/api/admin/integrity-check/${runId}`);
        if (!pollRes.ok) {
          setLoading(false);
          return;
        }
        const pollJson = await pollRes.json();
        const run = pollJson.data as IntegrityCheckRun;
        setCurrent(run);

        if (run.status === 'done' || run.status === 'failed') {
          setLoading(false);
          setLatest(run);
          return;
        }
        setTimeout(poll, 2000);
      };
      poll();
    } catch (err) {
      setLoading(false);
      throw err;
    }
  }, []);

  return { latest, current, loading, fetchLatest, trigger };
}
